import axioslib, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { AuthInterceptor } from "./AuthInterceptor";
import VersionInterceptor from "@/api/VersionInterceptor";
import { Logger } from "@iloq/shared";
import {
  IMaintenanceReturnResult,
  IProductReturn,
  ISalesReturnRequest,
  ICampaignReturnRequest,
  IResultEntity,
  IWarrantyInfo,
  ShipperType,
} from "@iloq/shared";
import { WarrantyInfoNotFoundError } from "@/WarrantyInfoNotFoundError";

export default class ReturnsApi {
  private readonly axios: AxiosInstance;

  readonly returnTypes = {
    maintenanceReturn: "MAINTENANCE_RETURN",
    salesReturn: "SALES_RETURN",
  };

  constructor() {
    this.axios = axioslib.create();
    AuthInterceptor.setAuthInterceptors(this.axios);
    VersionInterceptor.setVersionInterceptor(this.axios);
  }

  createProductReturn(
    request: ISalesReturnRequest | ICampaignReturnRequest
  ): Promise<IResultEntity | IMaintenanceReturnResult> {
    const options: AxiosRequestConfig = {
      method: "POST",
      url: "/api/v1/returns/productReturns",
      data: request,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("ReturnsApi.productReturns failed:", error);
        return Promise.reject(error);
      })
      .then((result: any) => {
        Logger.debug("ReturnsApi.productReturns result data:", result.data);
        return result.data;
      });
  }

  listProductReturns(email: string): Promise<IProductReturn[]> {
    const options: AxiosRequestConfig = {
      method: "GET",
      url: `/api/v1/returns/productReturns/${email}`,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("ReturnsApi.productReturns (list) failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("ReturnsApi.productReturns result data:", result.data);
        return result.data as IProductReturn[];
      });
  }

  getShipperType(countryCode: string, zipCode: string, city: string): Promise<ShipperType> {
    const options: AxiosRequestConfig = {
      method: "GET",
      url: `/api/v1/returns/productreturns/shippertype/${countryCode}/${zipCode}/${city}`,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("ReturnsApi.getShipperType failed:", error);
        return Promise.reject(error);
      })
      .then((result: AxiosResponse) => {
        Logger.debug("ReturnsApi.getShipperType result", result.data);
        return result.data as ShipperType;
      });
  }

  getWarrantyInfo(serialNumber: string): Promise<IWarrantyInfo> {
    const options: AxiosRequestConfig = {
      method: "GET",
      url: `/api/v1/returns/productreturns/warrantyinfo/${serialNumber}`,
    };
    return this.axios(options)
      .catch((error: AxiosError) => {
        if (error.response?.status === 404) {
          Logger.debug(
            `ReturnsApi.getWarrantyInfo not found for given serialNumber: ${serialNumber}`
          );
          return Promise.reject(new WarrantyInfoNotFoundError("Warranty end not found"));
        }

        Logger.error("ReturnsApi.getWarrantyInfo failed:", error);
        return Promise.reject(error);
      })
      .then((result: AxiosResponse) => {
        Logger.debug("ReturnsApi.getWarrantyInfo result", result.data);
        return result.data as IWarrantyInfo;
      });
  }
}
